/**
 * AdminTenantScopeBar — 管理员租户范围选择器
 *
 * Shared component for admin pages: lets platform_admin scope their view
 * to a specific tenant or "All Tenants". Renders a compact select bar.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Building2, ChevronDown, Globe } from 'lucide-react';
import { api } from '@/lib/api';

interface TenantOption {
  id: string;
  name: string;
  slug: string;
  plan: string;
  deployment_ring: string;
}

interface AdminTenantScopeBarProps {
  value: string | null; // null = all tenants
  onChange: (tenantId: string | null) => void;
  showRingBadge?: boolean;
  className?: string;
}

const RING_BADGE: Record<string, string> = {
  'ring-0': 'bg-purple-100 text-purple-700',
  'ring-1': 'bg-blue-100 text-blue-700',
  'ring-2': 'bg-green-100 text-green-700',
  'ring-3': 'bg-gray-100 text-gray-600',
};

export default function AdminTenantScopeBar({
  value,
  onChange,
  showRingBadge = true,
  className = '',
}: AdminTenantScopeBarProps) {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.get<TenantOption[]>('/platform/tenant-scope-list');
      setTenants(data);
    } catch {
      // Fallback: ignore
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = value ? tenants.find(t => t.id === value) : null;

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-w-[200px]"
      >
        {selected ? (
          <>
            <Building2 className="w-4 h-4 text-blue-500" />
            <span className="truncate max-w-[160px]">{selected.name}</span>
            {showRingBadge && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${RING_BADGE[selected.deployment_ring] ?? ''}`}>
                {selected.deployment_ring}
              </span>
            )}
          </>
        ) : (
          <>
            <Globe className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">All Tenants</span>
          </>
        )}
        <ChevronDown className="w-3 h-3 ml-auto opacity-50" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-gray-800 border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {/* All tenants option */}
          <button
            onClick={() => { onChange(null); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${!value ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
          >
            <Globe className="w-4 h-4 text-gray-400" />
            All Tenants
          </button>
          <div className="border-t" />
          {tenants.map(t => (
            <button
              key={t.id}
              onClick={() => { onChange(t.id); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${value === t.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
            >
              <Building2 className="w-3 h-3 text-gray-400" />
              <span className="truncate flex-1 text-left">{t.name}</span>
              <span className="text-xs text-gray-400">{t.slug}</span>
              {showRingBadge && (
                <span className={`text-xs px-1 py-0.5 rounded ${RING_BADGE[t.deployment_ring] ?? ''}`}>
                  {t.deployment_ring}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Click-away */}
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
}
